import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AuthService } from '../../../api/auth.service';
import { RoutesService } from '../../../api/routes.service';
import { LoginGuardService } from '../../../api/login-guard.service';
import { LangService } from '../../../core/lang.service';
import {
  IMarkingWindowTcLink,
  markingWindowScorLeadLink,
  markingWindowTcLink,
} from '../marking-window-routes';
import {
  BLACKOUT_TIMEZONE_OPTIONS,
  DEFAULT_BLACKOUT_TIMEZONE,
  IBlackoutPreviewGroup,
  IDisallowedTimeRange,
  IDisallowedTimeRangeGroup,
  IMarkingWindowLabel,
  IMarkingWindowSchedule,
  buildNewGroupPayload,
  describeBlackoutApiError,
  editorStateAfterSave,
  hasUnsavedBlackoutEdits,
  cloneRangeForEdit,
  cloneRangesForEdit,
  isGroupSelected,
  isGroupSelectionDirty,
  markingWindowLabel,
  newDateRange,
  newRecurringRange,
  otherAttachedWindows,
  otherWindowsForGroups,
  parseMarkingTasks,
  rangesEqual,
  serializeRanges,
  toggleGroupSelection,
  validateAcademicYear,
  validateRange,
  validateRanges,
} from '../types';

interface ILoadScheduleOptions {
  /** Keep the panel mounted so scroll position in the group/rule columns is not lost. */
  silent?: boolean;
  /** Revert the checked set and editor to attached groups (initial load / apply). */
  resetEditor?: boolean;
}

interface IOpenGroupForEditOptions {
  ruleIndex?: number | null;
  preserveDateSections?: boolean;
}

/**
 * DELIV-17370 — non-working (blackout) hours panel on Scoring Window Setup.
 *
 * Assignment (which groups are linked to this scoring window) is separate from
 * editing a group's rule rows. A window can enforce several groups at once —
 * their rules are additive — so the picker is multi-select and the rule editor
 * works on one chosen group at a time.
 */
@Component({
  selector: 'blackout-hours-window-panel',
  templateUrl: './blackout-hours-window-panel.component.html',
  styleUrls: ['./blackout-hours-window-panel.component.scss']
})
export class BlackoutHoursWindowPanelComponent implements OnChanges {

  /** `marking_windows.id` of the scoring window being configured. */
  @Input() markingWindowId: number;
  @Input() testWindowId: number;
  @Input() testCtrlGroupId: number;
  @Input() disabled: boolean = false;
  /** Which API mount to call — Test Controller or Scoring Leader. */
  @Input() apiContext: 'test-ctrl' | 'scor-lead' = 'test-ctrl';
  /** Hide the TC scoring-window-setup link (Scoring Leader page). */
  @Input() showTcWindowLink: boolean = true;

  TIMEZONE_OPTIONS = BLACKOUT_TIMEZONE_OPTIONS;

  schedule: IMarkingWindowSchedule;
  isLoading: boolean = false;
  isSaving: boolean = false;
  isApplying: boolean = false;
  loadError: string = null;

  /** Groups checked for this window, pending apply. */
  selectedGroupIds: number[] = [];
  /** Group whose rules are open in the editor; 0 when none. */
  editingGroupId: number = 0;
  /** True while the new-group form is open. */
  isCreatingGroup: boolean = false;
  /** Rules being edited for `editingGroupId` (or the new group). */
  editRanges: IDisallowedTimeRange[] = [];
  ruleSnapshots: IDisallowedTimeRange[] = [];
  selectedRuleIndex: number | null = null;
  dateSectionOpenIndices = new Set<number>();

  newGroupName: string = '';
  newGroupTimezone: string = DEFAULT_BLACKOUT_TIMEZONE;
  /** Prefilled from the scoring window; the API requires it on create. */
  newGroupAcademicYear: string = '';
  editTimezone: string = DEFAULT_BLACKOUT_TIMEZONE;
  isShowHistory: boolean = false;

  /** Stable reference for schedule preview — do not use a getter (infinite CD loop). */
  previewGroups: IBlackoutPreviewGroup[] = [];

  readonly OFF = 0;

  markingWindowLabel = markingWindowLabel;

  get scheduleRoute(): string {
    return this.apiContext === 'scor-lead'
      ? this.routes.SCOR_LEAD_MARKING_WINDOW_SCHEDULE
      : this.routes.TEST_CTRL_MARKING_WINDOW_SCHEDULE;
  }

  get groupsRoute(): string {
    return this.apiContext === 'scor-lead'
      ? this.routes.SCOR_LEAD_DISALLOWED_TIME_RANGE_GROUPS
      : this.routes.TEST_CTRL_DISALLOWED_TIME_RANGE_GROUPS;
  }

  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private loginGuard: LoginGuardService,
    private lang: LangService,
  ) { }

  get isEditingRules(): boolean {
    return this.isCreatingGroup || this.editingGroupId > 0;
  }

  /**
   * Preview overlays every checked group, so the leader sees the combined
   * blackout the scorer will hit — not just the group being edited.
   */
  syncPreviewGroups(): void {
    const groups: IBlackoutPreviewGroup[] = [];
    if (this.isCreatingGroup) {
      groups.push({
        key: 'draft',
        name: this.newGroupName.trim() || 'New group',
        timezone: this.newGroupTimezone,
        ranges: this.editRanges,
      });
    }
    this.selectedGroups.forEach((group) => {
      const isEditing = Number(group.id) === Number(this.editingGroupId);
      groups.push({
        key: String(group.id),
        id: group.id,
        name: group.name,
        // Unsaved edits belong to the group in the editor only.
        timezone: isEditing ? this.editTimezone : group.timezone,
        ranges: isEditing ? this.editRanges : (group.ranges || []),
      });
    });
    this.previewGroups = groups;
  }

  get previewScheduleOn(): boolean {
    return this.isCreatingGroup || this.selectedGroupIds.length > 0;
  }

  /** Collapse key for schedule preview — kept out of the template (no `??` in Angular 9). */
  get previewContextKey(): string {
    if (this.isCreatingGroup) return 'new';
    return this.selectedGroupIds.length
      ? this.selectedGroupIds.join(',')
      : 'off';
  }

  markingWindowLink(windowId: number): IMarkingWindowTcLink | null {
    return markingWindowTcLink(
      this.lang.c(),
      windowId,
      this.testCtrlGroupId,
      this.testWindowId,
    );
  }

  /**
   * Where a window named in the scope warning is configured: the Scoring Leader
   * page for its own non-working hours, or the Test Controller window editor.
   */
  scopeWindowLink(windowId: number): IMarkingWindowTcLink | null {
    if (this.apiContext === 'scor-lead') {
      return markingWindowScorLeadLink(this.lang.c(), windowId);
    }
    return this.markingWindowLink(windowId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.markingWindowId && this.markingWindowId) {
      this.loadSchedule();
    }
  }

  async loadSchedule(options?: ILoadScheduleOptions) {
    if (!this.markingWindowId) return;
    const silent = options?.silent === true;
    const resetEditor = options?.resetEditor !== false;
    if (!silent) {
      this.isLoading = true;
    }
    this.loadError = null;
    try {
      this.schedule = await this.auth.apiGet(
        this.scheduleRoute,
        this.markingWindowId,
      );
      if (resetEditor) {
        this.resetEditState();
      }
    } catch (err) {
      this.loadError = describeBlackoutApiError(err, 'Could not load non-working hours.');
    } finally {
      if (!silent) {
        this.isLoading = false;
      }
    }
  }

  private resetEditState() {
    this.selectedGroupIds = [...this.activeGroupIds];
    this.isCreatingGroup = false;
    this.newGroupName = '';
    this.newGroupTimezone = DEFAULT_BLACKOUT_TIMEZONE;
    // Open the first attached group so the rule editor is not empty on load.
    this.openGroupForEdit(this.selectedGroupIds[0] || this.OFF);
  }

  get groups(): IDisallowedTimeRangeGroup[] {
    return this.schedule?.groups || [];
  }

  /** Editing is pointless before the `mw_*` tables exist. */
  get isReadOnly(): boolean {
    return this.disabled || this.schedule?.is_schema_ready === false;
  }

  /** The API reports null until `marking_windows.is_nwh_resume_time_hidden` is deployed. */
  get isResumeTimeFlagAvailable(): boolean {
    const flag = this.schedule?.marking_window?.is_nwh_resume_time_hidden;
    return flag !== null && flag !== undefined;
  }

  get isResumeTimeHidden(): boolean {
    return Number(this.schedule?.marking_window?.is_nwh_resume_time_hidden) === 1;
  }

  isSavingResumeFlag = false;

  onResumeFlagChange(event: Event) {
    this.setResumeTimeHidden(!!(event.target as HTMLInputElement)?.checked);
  }

  async setResumeTimeHidden(isHidden: boolean) {
    if (this.isReadOnly || this.isSavingResumeFlag || !this.isResumeTimeFlagAvailable) return;
    this.isSavingResumeFlag = true;
    try {
      // Flag-only payload: the API leaves group attachments alone when no group ids are sent.
      await this.auth.apiPatch(
        this.scheduleRoute,
        this.markingWindowId,
        { is_nwh_resume_time_hidden: isHidden ? 1 : 0 },
      );
      if (this.schedule?.marking_window) {
        this.schedule.marking_window.is_nwh_resume_time_hidden = isHidden ? 1 : 0;
      }
    } catch (err) {
      this.loginGuard.quickPopup(describeBlackoutApiError(err, 'Could not save the resume time setting.'));
    } finally {
      this.isSavingResumeFlag = false;
    }
  }

  /** Academic year from the scoring window schedule (shown when creating a new group). */
  get windowAcademicYear(): string {
    const schedule = this.schedule;
    if (!schedule) {
      return '';
    }
    return schedule.window_academic_year
      || (schedule.marking_window && schedule.marking_window.academic_year)
      || '';
  }

  /** Groups attached on the server right now. */
  get activeGroups(): IDisallowedTimeRangeGroup[] {
    const schedule = this.schedule;
    if (!schedule) return [];
    if (schedule.activeGroups) return schedule.activeGroups;
    return schedule.activeGroup ? [schedule.activeGroup] : [];
  }

  get activeGroupIds(): number[] {
    return this.activeGroups.map((group) => Number(group.id)).filter(Boolean);
  }

  /** Checked groups, resolved against the full group list. */
  get selectedGroups(): IDisallowedTimeRangeGroup[] {
    return this.selectedGroupIds
      .map((id) => this.groups.find((group) => Number(group.id) === Number(id)))
      .filter(Boolean);
  }

  get editingGroup(): IDisallowedTimeRangeGroup {
    return this.groups.find((group) => Number(group.id) === Number(this.editingGroupId));
  }

  /** Windows other than this one reached by editing the group in the editor. */
  get sharedWindows(): IMarkingWindowLabel[] {
    if (this.isCreatingGroup) return [];
    return otherAttachedWindows(this.editingGroup, this.markingWindowId);
  }

  /** Windows reached by any checked group — shown next to the apply button. */
  get sharedWindowsForSelection(): IMarkingWindowLabel[] {
    return otherWindowsForGroups(this.selectedGroups, this.markingWindowId);
  }

  get isAttachmentUnchanged(): boolean {
    return !isGroupSelectionDirty(this.selectedGroupIds, this.activeGroupIds);
  }

  get assignmentStatusLabel(): string {
    const active = this.activeGroups;
    if (!active.length) {
      return 'No group is attached to this scoring window — non-working hours are not enforced.';
    }
    if (active.length === 1) {
      return `Scoring window ${this.markingWindowId} uses “${active[0].name}” (${active[0].timezone}).`;
    }
    const names = active.map((group) => `“${group.name}”`).join(', ');
    return `Scoring window ${this.markingWindowId} uses ${active.length} groups: ${names}. Their rules are combined.`;
  }

  get pendingAssignmentLabel(): string {
    if (this.isAttachmentUnchanged) return null;
    if (this.selectedGroupIds.length === 0) {
      return 'Apply to turn non-working hours off for this scoring window.';
    }
    const names = this.selectedGroups.map((group) => `“${group.name}”`).join(', ');
    const count = this.selectedGroupIds.length;
    return count === 1
      ? `Apply to attach ${names} to scoring window ${this.markingWindowId}.`
      : `Apply to attach ${count} groups to scoring window ${this.markingWindowId}: ${names}.`;
  }

  /** Unsaved new-group draft, or rule / timezone edits on the open group. */
  get hasUnsavedEdits(): boolean {
    return hasUnsavedBlackoutEdits({
      isCreatingGroup: this.isCreatingGroup,
      newGroupName: this.newGroupName,
      editRanges: this.editRanges,
      editTimezone: this.editTimezone,
      savedGroup: this.editingGroup,
    });
  }

  /** True when there is nothing to lose, or the leader agrees to lose it. */
  private confirmDiscard(): boolean {
    if (!this.hasUnsavedEdits) return true;
    const what = this.isCreatingGroup ? 'the new group you have not saved' : 'your unsaved rule changes';
    return confirm(`Discard ${what}?`);
  }

  /** Selection change from the multi-select picker. */
  onSelectedGroupIdsChange(groupIds: number[]) {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    const nextIds = (groupIds || []).map(Number).filter(Boolean);
    // A new-group draft stays open: Apply creates it and attaches it with the checked groups.
    if (this.isCreatingGroup) {
      this.selectedGroupIds = nextIds;
      this.syncPreviewGroups();
      return;
    }
    // Keep the editor on a group that is still checked.
    if (isGroupSelected(nextIds, this.editingGroupId)) {
      this.selectedGroupIds = nextIds;
      this.syncPreviewGroups();
      return;
    }
    if (!this.confirmDiscard()) {
      // The picker's checkbox already moved; hand it the rejected state, then the kept one.
      const kept = this.selectedGroupIds;
      this.selectedGroupIds = nextIds;
      setTimeout(() => { this.selectedGroupIds = kept; });
      return;
    }
    this.selectedGroupIds = nextIds;
    this.openGroupForEdit(nextIds[0] || this.OFF);
  }

  /** "Edit rules" / row click on a checked group from the picker. */
  onEditGroupRequested(groupId: number) {
    const id = Number(groupId) || this.OFF;
    if (!this.isCreatingGroup && id === Number(this.editingGroupId)) return;
    if (!this.confirmDiscard()) return;
    this.openGroupForEdit(id);
  }

  onToggleGroup(groupId: number) {
    this.onSelectedGroupIdsChange(toggleGroupSelection(this.selectedGroupIds, groupId));
  }

  /** Load a checked group's rules into the editor. */
  openGroupForEdit(groupId: number, options?: IOpenGroupForEditOptions) {
    const id = Number(groupId) || this.OFF;
    this.isCreatingGroup = false;
    this.editingGroupId = id;
    const group = this.groups.find((g) => Number(g.id) === id);
    this.editRanges = id > 0 ? cloneRangesForEdit(group) : [];
    this.editTimezone = group?.timezone || DEFAULT_BLACKOUT_TIMEZONE;
    if (options && Object.prototype.hasOwnProperty.call(options, 'ruleIndex')) {
      this.selectedRuleIndex = options.ruleIndex;
    } else {
      this.selectedRuleIndex = this.editRanges.length ? 0 : null;
    }
    if (!options?.preserveDateSections) {
      this.dateSectionOpenIndices.clear();
    }
    this.syncRuleSnapshots();
    this.syncPreviewGroups();
  }

  /** Open the new-group form without disturbing the checked set. */
  startCreateGroup() {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    if (!this.confirmDiscard()) return;
    this.isCreatingGroup = true;
    this.editingGroupId = this.OFF;
    this.editRanges = [];
    this.editTimezone = DEFAULT_BLACKOUT_TIMEZONE;
    this.newGroupName = '';
    this.newGroupTimezone = DEFAULT_BLACKOUT_TIMEZONE;
    this.newGroupAcademicYear = this.windowAcademicYear;
    this.selectedRuleIndex = null;
    this.dateSectionOpenIndices.clear();
    this.syncRuleSnapshots();
    this.syncPreviewGroups();
  }

  /** "Off" row — clear every checked group. */
  clearGroupSelection() {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    if (!this.confirmDiscard()) return;
    this.selectedGroupIds = [];
    this.isCreatingGroup = false;
    this.openGroupForEdit(this.OFF);
  }

  get selectedRuleValidationError(): string | null {
    if (this.selectedRuleIndex == null) return null;
    return validateRange(this.editRanges[this.selectedRuleIndex]);
  }

  get isSelectedRuleDirty(): boolean {
    if (this.selectedRuleIndex == null) return false;
    const snap = this.ruleSnapshots[this.selectedRuleIndex];
    const current = this.editRanges[this.selectedRuleIndex];
    if (!snap || !current) return false;
    return !rangesEqual(snap, current);
  }

  get canSaveRule(): boolean {
    if (this.selectedRuleIndex == null || this.isSaving || this.isApplying || this.isReadOnly) return false;
    if (this.selectedRuleValidationError) return false;
    if (!this.isCreatingGroup && (this.rulesValidationError || !this.canSaveRules)) return false;
    return true;
  }

  private syncRuleSnapshots() {
    this.ruleSnapshots = this.editRanges.map((r) => cloneRangeForEdit(r));
  }

  private syncRuleSnapshotsAfterRemove(removedIndex: number) {
    this.ruleSnapshots = this.ruleSnapshots.filter((_, i) => i !== removedIndex);
  }

  private syncRuleSnapshotsAfterInsert(insertIndex: number) {
    const snap = cloneRangeForEdit(this.editRanges[insertIndex]);
    const next = [...this.ruleSnapshots];
    next.splice(insertIndex, 0, snap);
    this.ruleSnapshots = next;
  }

  async saveRule() {
    if (!this.canSaveRule) return;
    const index = this.selectedRuleIndex;
    if (this.isCreatingGroup) {
      this.ruleSnapshots[index] = cloneRangeForEdit(this.editRanges[index]);
      return;
    }
    if (!this.canSaveRules) return;
    await this.saveGroupRules();
    if (index != null && this.editRanges[index]) {
      this.ruleSnapshots[index] = cloneRangeForEdit(this.editRanges[index]);
    }
  }

  resetSelectedRule() {
    if (this.selectedRuleIndex == null || this.isSaving || this.isApplying || this.isReadOnly) return;
    const snap = this.ruleSnapshots[this.selectedRuleIndex];
    if (!snap) return;
    this.onRuleChange(this.selectedRuleIndex, cloneRangeForEdit(snap));
  }

  onSelectedRuleIndexChange(index: number | null) {
    this.selectedRuleIndex = index;
  }

  onRuleChange(index: number, range: IDisallowedTimeRange) {
    if (!Array.isArray(this.editRanges) || index < 0 || index >= this.editRanges.length) return;
    this.editRanges[index] = range;
    this.editRanges = [...this.editRanges];
    this.syncPreviewGroups();
  }

  isDateSectionOpen(index: number): boolean {
    const range = this.editRanges[index];
    return !!(range?.date_start || range?.date_end) || this.dateSectionOpenIndices.has(index);
  }

  setDateSectionOpen(index: number, open: boolean) {
    if (open) this.dateSectionOpenIndices.add(index);
    else this.dateSectionOpenIndices.delete(index);
  }

  addRecurringRule() {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    this.editRanges = [...this.editRanges, newRecurringRange()];
    this.ruleSnapshots.push(cloneRangeForEdit(this.editRanges[this.editRanges.length - 1]));
    this.selectedRuleIndex = this.editRanges.length - 1;
    this.syncPreviewGroups();
  }

  addDateRule() {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    this.editRanges = [...this.editRanges, newDateRange(false)];
    this.ruleSnapshots.push(cloneRangeForEdit(this.editRanges[this.editRanges.length - 1]));
    this.selectedRuleIndex = this.editRanges.length - 1;
    this.dateSectionOpenIndices.add(this.selectedRuleIndex);
    this.syncPreviewGroups();
  }

  revokeRule(index: number) {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    const range = this.editRanges[index];
    if (range?.id && !confirm('Revoke this rule? It will stop applying after you save the group rules.')) {
      return;
    }
    this.editRanges = this.editRanges.filter((_, i) => i !== index);
    this.syncRuleSnapshotsAfterRemove(index);
    this.syncIndicesAfterRemove(index);
    if (this.selectedRuleIndex === index) {
      this.selectedRuleIndex = this.editRanges.length ? Math.min(index, this.editRanges.length - 1) : null;
    } else if (this.selectedRuleIndex != null && this.selectedRuleIndex > index) {
      this.selectedRuleIndex -= 1;
    }
    this.syncPreviewGroups();
  }

  duplicateRule(index: number) {
    if (this.isReadOnly || this.isApplying || this.isSaving) return;
    const source = this.editRanges[index];
    const copy = { ...source, marking_tasks: parseMarkingTasks(source.marking_tasks) };
    delete copy.id;
    const insertIndex = index + 1;
    const next = [...this.editRanges];
    next.splice(insertIndex, 0, copy);
    this.editRanges = next;
    this.syncRuleSnapshotsAfterInsert(insertIndex);
    this.syncIndicesAfterInsert(insertIndex);
    this.selectedRuleIndex = insertIndex;
    this.syncPreviewGroups();
  }

  private syncIndicesAfterRemove(removedIndex: number) {
    const nextDates = new Set<number>();
    for (const i of this.dateSectionOpenIndices) {
      if (i < removedIndex) nextDates.add(i);
      else if (i > removedIndex) nextDates.add(i - 1);
    }
    this.dateSectionOpenIndices = nextDates;
  }

  private syncIndicesAfterInsert(insertIndex: number) {
    const nextDates = new Set<number>();
    for (const i of this.dateSectionOpenIndices) {
      nextDates.add(i >= insertIndex ? i + 1 : i);
    }
    this.dateSectionOpenIndices = nextDates;
  }

  get createValidationError(): string {
    if (!this.isCreatingGroup) return null;
    if (!this.newGroupName.trim()) return 'Enter a name for the new group.';
    return validateAcademicYear(this.newGroupAcademicYear) || validateRanges(this.editRanges);
  }

  get rulesValidationError(): string {
    if (this.isCreatingGroup || this.editingGroupId <= 0) return null;
    return validateRanges(this.editRanges);
  }

  get canApplySchedule(): boolean {
    if (this.isReadOnly || this.isApplying || this.isSaving) return false;
    if (this.isCreatingGroup) return !this.createValidationError;
    return true;
  }

  get canSaveRules(): boolean {
    if (this.isReadOnly || this.isSaving || this.isApplying) return false;
    if (this.isCreatingGroup) return !this.createValidationError;
    if (this.editingGroupId <= 0) return false;
    return !this.rulesValidationError;
  }

  /**
   * Apply the checked set to this scoring window only. When the new-group form
   * is open, the group is created and added to the set in the same step.
   */
  async applySchedule() {
    if (!this.canApplySchedule) return;
    this.isApplying = true;
    const wasCreating = this.isCreatingGroup;
    const createdName = wasCreating ? this.newGroupName.trim() : '';
    const appliedOff = !wasCreating && this.selectedGroupIds.length === 0;
    const appliedCount = this.selectedGroupIds.length;
    const appliedNames = this.selectedGroups.map((group) => group.name).join(', ');
    try {
      if (wasCreating) {
        const created: any = await this.auth.apiCreate(this.groupsRoute, this.newGroupPayload());
        // Keep the groups already checked and add the new one.
        await this.attachGroups([...this.selectedGroupIds, Number(created?.id)]);
      } else {
        await this.attachGroups(this.selectedGroupIds);
      }
      await this.loadSchedule();
      if (appliedOff) {
        this.loginGuard.quickPopup('Non-working hours turned off for this scoring window.');
      } else if (wasCreating) {
        this.loginGuard.quickPopup(`Created and applied “${createdName}” to scoring window ${this.markingWindowId}.`);
      } else if (appliedCount === 1) {
        this.loginGuard.quickPopup(`Applied “${appliedNames}” to scoring window ${this.markingWindowId}.`);
      } else {
        this.loginGuard.quickPopup(`Applied ${appliedCount} groups to scoring window ${this.markingWindowId}.`);
      }
    } catch (err) {
      this.loginGuard.quickPopup(describeBlackoutApiError(err, 'Could not apply the schedule to this scoring window.'));
    } finally {
      this.isApplying = false;
    }
  }

  /**
   * Create the group from the new-group form without attaching it. It joins the
   * checked set and opens in the editor; "Apply to scoring window" still does the attach.
   */
  private async saveNewGroup() {
    this.isSaving = true;
    const name = this.newGroupName.trim();
    const checkedBefore = [...this.selectedGroupIds];
    try {
      const created: any = await this.auth.apiCreate(this.groupsRoute, this.newGroupPayload());
      const createdId = Number(created?.id);
      await this.loadSchedule({ silent: true, resetEditor: false });
      this.selectedGroupIds = [...new Set([...checkedBefore, createdId].filter(Boolean))];
      this.openGroupForEdit(createdId);
      this.loginGuard.quickPopup(`Saved “${name}”. Click Apply to scoring window to attach it to scoring window ${this.markingWindowId}.`);
    } catch (err) {
      this.loginGuard.quickPopup(describeBlackoutApiError(err, 'Could not save the new group.'));
    } finally {
      this.isSaving = false;
    }
  }

  /** Persist rule edits on the group in the editor (shared by every window using it). */
  async saveGroupRules() {
    if (!this.canSaveRules) return;
    if (this.isCreatingGroup) {
      return this.saveNewGroup();
    }
    this.isSaving = true;
    const group_id = this.editingGroupId;
    const checkedBefore = [...this.selectedGroupIds];
    const ruleIndexBefore = this.selectedRuleIndex;
    try {
      await this.auth.apiPatch(
        this.groupsRoute,
        group_id,
        { timezone: this.editTimezone, ranges: serializeRanges(this.editRanges) },
      );
      await this.loadSchedule({ silent: true, resetEditor: false });
      const savedGroup = this.groups.find((g) => Number(g.id) === Number(group_id));
      const restored = editorStateAfterSave({
        checkedBefore,
        savedGroupId: group_id,
        ruleIndexBefore,
        ruleCountAfter: cloneRangesForEdit(savedGroup).length,
      });
      this.selectedGroupIds = restored.selectedGroupIds;
      this.openGroupForEdit(group_id, {
        ruleIndex: restored.selectedRuleIndex,
        preserveDateSections: true,
      });
    } catch (err) {
      this.loginGuard.quickPopup(describeBlackoutApiError(err, 'Could not save the group rules.'));
    } finally {
      this.isSaving = false;
    }
  }

  private newGroupPayload() {
    return buildNewGroupPayload({
      name: this.newGroupName,
      timezone: this.newGroupTimezone,
      academicYear: this.newGroupAcademicYear,
      ranges: this.editRanges,
    });
  }

  private attachGroups(groupIds: number[]) {
    const ids = [...new Set((groupIds || []).map(Number).filter(Boolean))];
    return this.auth.apiPatch(
      this.scheduleRoute,
      this.markingWindowId,
      { mw_disallowed_time_range_group_ids: ids },
    );
  }

  cancel() {
    if (!this.confirmDiscard()) return;
    this.resetEditState();
  }

  toggleHistory() {
    this.isShowHistory = !this.isShowHistory;
  }
}
